import ProtectedPageHeader from 'components/ProtectedPageHeader'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import { buildTranslate } from 'locales'
import React, { useMemo } from 'react'
import { Container } from 'react-bootstrap-five'
import Tab from 'react-bootstrap-five/Tab'
import Helmet from 'react-helmet'
import { I18n } from 'react-redux-i18n'
import { Redirect } from 'react-router-dom'
import LookerFooter from 'scenes/LookerEmbed/components/LookerFooter'
import LookerIframe from 'scenes/LookerEmbed/LookerIframe'

import { Tabs, usePromoCodeReport } from './usePromoCodeReport'

const DASHBOARD_HEIGHT = 1500
const LOOKER_FOOTER_OFFSET = 300

const t = buildTranslate('looker.promo_code_report')

const PromoCodeReport = () => {
  const {
    promoCodesEnabled,
    dashboardIds,
    merchant,
    css,
    activeTab,
    onTabChange,
    reportPermission,
  } = usePromoCodeReport()

  // Memoize so the styletron css() call only re-hashes when the layout
  // constants change, not on every parent render.
  const containerClass = useMemo(
    () =>
      `px-0 ${css({
        minHeight: `${DASHBOARD_HEIGHT + LOOKER_FOOTER_OFFSET}px`,
      })}`,
    [css]
  )

  if (!promoCodesEnabled) return <Redirect to="/" />
  if (!merchant) return null

  // Render only the active tab's Looker iframe. React-Bootstrap mounts every
  // TabPane up front, which would fire four Looker requests on page load; this
  // limits the network cost to one iframe at a time. The LookerFooter is cheap
  // and stays rendered so the layout doesn't shift between tabs.
  const renderDashboard = (tab: Tabs, path: string) =>
    activeTab === tab && (
      <LookerIframe
        path={path}
        merchant={merchant}
        height={`${DASHBOARD_HEIGHT}px`}
      />
    )

  return (
    <>
      <Helmet>
        <title>{I18n.t(`titles./reports/promo_codes#${activeTab}`)}</title>
      </Helmet>
      <ProtectedPageHeader
        title={t('title')}
        resource={reportPermission.resource}
        action={reportPermission.action}
      >
        <Tab.Container
          activeKey={activeTab}
          transition={false}
          onSelect={onTabChange}
        >
          <TabNav>
            <TabItem
              state={Tabs.CAMPAIGN_PERFORMANCE}
              title={t('tabs.campaign_performance')}
            />
            <TabItem
              state={Tabs.CODE_UTILIZATION}
              title={t('tabs.code_utilization')}
            />
            <TabItem
              state={Tabs.GEOGRAPHIC_BREAKDOWN}
              title={t('tabs.geographic_breakdown')}
            />
            <TabItem
              state={Tabs.CHANNEL_BREAKDOWN}
              title={t('tabs.channel_breakdown')}
            />
          </TabNav>
          <Tab.Content className="pb-l h-100 grey-05-bkg">
            <TabPane state={Tabs.CAMPAIGN_PERFORMANCE}>
              <Container className={containerClass}>
                {renderDashboard(
                  Tabs.CAMPAIGN_PERFORMANCE,
                  dashboardIds.campaign_performance
                )}
                <LookerFooter className="my-xl" />
              </Container>
            </TabPane>
            <TabPane state={Tabs.CODE_UTILIZATION}>
              <Container className={containerClass}>
                {renderDashboard(
                  Tabs.CODE_UTILIZATION,
                  dashboardIds.code_utilization
                )}
                <LookerFooter className="my-xl" />
              </Container>
            </TabPane>
            <TabPane state={Tabs.GEOGRAPHIC_BREAKDOWN}>
              <Container className={containerClass}>
                {renderDashboard(
                  Tabs.GEOGRAPHIC_BREAKDOWN,
                  dashboardIds.geographic_breakdown
                )}
                <LookerFooter className="my-xl" />
              </Container>
            </TabPane>
            <TabPane state={Tabs.CHANNEL_BREAKDOWN}>
              <Container className={containerClass}>
                {renderDashboard(
                  Tabs.CHANNEL_BREAKDOWN,
                  dashboardIds.channel_breakdown
                )}
                <LookerFooter className="my-xl" />
              </Container>
            </TabPane>
          </Tab.Content>
        </Tab.Container>
      </ProtectedPageHeader>
    </>
  )
}

export default PromoCodeReport
